import { useQuery } from '@hooks/reactQuery';

import {
	getAlarmOranAlarmIds,
	getAlarmOranAlarmIdsActive,
	getAlarmOranApplicabilities,
	getAlarmOranApplicabilitiesActive,
	getAlarmOranGroups,
	getAlarmOranGroupsActive,
	getAlarmOranModuleNames,
	getAlarmOranModuleNamesActive,
	getAlarmOranSeverities,
	getAlarmOranSeveritiesActive,
	getAlarmOranStatuses,
	getOranAcknowledgedBy,
	getOranAcknowledgedByForAlarmsActive,
	getOranClearedBy,
	getOranMiddlewaresForAlarms,
	getOranMiddlewaresForAlarmsActive,
	getOranNetworkElementsForAlarms,
	getOranNetworkElementsForAlarmsActive,
} from '@api/oranAlarms';

import { extractQueryParams } from '@utils/common';
import { mapDropdownOptions, optionsFormatted, optionsFormattedBySystemId } from '@utils/formatter';

import { IAlarmByVendorFilter, IAlarmFilter } from '@interfaces/alarms/alarm';
import { TNameTitle } from '@interfaces/common/common';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EUserRoles } from '@enums/common';

import { booleanOptions, refetchOptions } from '@constants/common';
import {
	ALARM_APPLICABILITIES_ORAN,
	ALARM_GROUPS_ORAN,
	ALARM_IDS_ORAN,
	ALARM_MODULE_NAMES_ORAN,
	ALARM_SEVERITIES_ORAN,
	ALARM_STATUSES_ORAN,
	ORAN_ALARM_ACKNOWLEDGED_BY,
	ORAN_ALARM_CLEARED_BY,
	ORAN_MIDDLEWARES,
	ORAN_NETWORK_ELEMENTS,
} from '@constants/queryKeys';

import { IAlarmFiltersProps } from './useActiveAlarmFilters';

export const useQueryOranActiveAlarmFilters = ({ vendor, isActiveTab }: IAlarmFiltersProps): IAlarmByVendorFilter => {
	const isEnabled = !!vendor;

	const {
		data: groups,
		isInitialLoading: isLoadingGroups,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_GROUPS_ORAN, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranGroupsActive) : getAlarmOranGroups,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		enabled: [isEnabled],
		...refetchOptions,
	});

	const {
		data: moduleNames,
		isInitialLoading: isLoadingModuleNames,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_MODULE_NAMES_ORAN, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranModuleNamesActive) : getAlarmOranModuleNames,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		enabled: [isEnabled],
		...refetchOptions,
	});

	const {
		data: severities,
		isInitialLoading: isLoadingSeverities,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_SEVERITIES_ORAN, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranSeveritiesActive) : getAlarmOranSeverities,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		enabled: [isEnabled],
		...refetchOptions,
	});

	const {
		data: alarmIds,
		isInitialLoading: isLoadingAlarmIds,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_IDS_ORAN, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranAlarmIdsActive) : getAlarmOranAlarmIds,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		enabled: [isEnabled],
		...refetchOptions,
	});

	const {
		data: applicabilities,
		isInitialLoading: isLoadingApplicabilities,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_APPLICABILITIES_ORAN, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranApplicabilitiesActive) : getAlarmOranApplicabilities,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		enabled: [isEnabled],
		...refetchOptions,
	});

	const {
		data: statuses,
		isInitialLoading: isLoadingStatuses,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_STATUSES_ORAN, { vendor }, isActiveTab],
		queryFn: getAlarmOranStatuses,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		enabled: [isEnabled],
		...refetchOptions,
	});

	const {
		data: middlewares,
		isInitialLoading: isLoadingMiddlewares,
	} = useQuery({
		queryKey: [ORAN_MIDDLEWARES, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getOranMiddlewaresForAlarmsActive) : getOranMiddlewaresForAlarms,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => optionsFormattedBySystemId(data),
	});

	const {
		data: networkElements,
		isInitialLoading: isLoadingNetworkElements,
	} = useQuery({
		queryKey: [ORAN_NETWORK_ELEMENTS, { vendor }, isActiveTab],
		queryFn: isActiveTab
			? extractQueryParams(getOranNetworkElementsForAlarmsActive)
			: getOranNetworkElementsForAlarms,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'id',
			labelKey: 'name',
			additionalMappings: {
				name: 'id',
			},
		}),
	});

	const {
		data: clearedByList,
		isInitialLoading: isLoadingClearedByList,
	} = useQuery({
		queryKey: [ORAN_ALARM_CLEARED_BY],
		queryFn: getOranClearedBy,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'clearedType.name',
			labelKey: 'clearedByTitle',
			additionalMappings: {
				userId: 'userId',
				name: 'clearedType.name',
			},
		}),
	});

	const {
		data: acknowledgedByList,
		isInitialLoading: isLoadingAcknowledgedByList,
	} = useQuery({
		queryKey: [ORAN_ALARM_ACKNOWLEDGED_BY, { vendor }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getOranAcknowledgedByForAlarmsActive) : getOranAcknowledgedBy,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'id',
			labelKey: 'fullName',
			additionalMappings: {
				name: 'id',
			},
		}),
	});

	const commonFilters: IAlarmFilter[] = [
		{
			name: 'alarmId',
			placeholder: 'Alarm ID',
			data: alarmIds || [],
		},
		{
			name: 'severity',
			placeholder: 'Severity',
			data: severities || [],
		},
		{
			name: 'group',
			placeholder: 'Group',
			data: groups || [],
		},
		{
			name: 'applicability',
			placeholder: 'Applicability',
			data: applicabilities || [],
		},
		{
			name: 'moduleName',
			placeholder: 'Module',
			data: moduleNames || [],
		},
		{
			name: 'networkElementId',
			placeholder: 'Network Element',
			data: networkElements || [],
		},
		{
			name: 'middlewareId',
			placeholder: 'Middleware',
			data: middlewares || [],
		},
		{
			name: 'isAcknowledged',
			placeholder: 'Acknowledged',
			data: booleanOptions,
		},
		{
			name: 'acknowledgeUserId',
			placeholder: 'Acknowledged by',
			data: acknowledgedByList || [],
		},
	];

	const historyFilters = [
		{
			name: 'status',
			placeholder: 'Status',
			data: statuses || [],
		},
		{
			name: 'clearedType',
			placeholder: 'Cleared by',
			data: clearedByList || [],
		},
	];

	const filters = !isActiveTab
		? [...commonFilters, ...historyFilters]
		: commonFilters;

	return {
		filters,
		isLoading: (
			isLoadingGroups ||
			isLoadingModuleNames ||
			isLoadingSeverities ||
			isLoadingApplicabilities ||
			isLoadingStatuses ||
			isLoadingAlarmIds ||
			isLoadingMiddlewares ||
			isLoadingNetworkElements ||
			isLoadingClearedByList ||
			isLoadingAcknowledgedByList
		),
	};
};
